import React, {useEffect, useRef, useState} from "react";
import { useReactToPrint } from "react-to-print";
import { withSidebar } from "../hoc/withSidebar";
import { useTranslation } from "react-i18next";
import ERPSBaseButton from "../custom/inputs/ERPSBaseButton";
import Breadcrumb from "../Breadcrumb/Breadcrumb";
import {useDispatch, useSelector} from "react-redux";
import ERPSTransactionDetails from "../custom/ERPSTransactionDetails";
import actions from "../../actions";
import ConfirmationMessage from "../custom/containers/ConfirmationMesssage";
import ErrorMessage from "../custom/containers/ErrorMessage";
import DialogHeader from "../custom/inputs/ERPSBaseModelDialog";
import ERPSBaseTextField from "../custom/inputs/ERPSBaseTextField";
import { Regex, RoleType }  from "../../constants/sharedConstants.js";
import { listProps, checkFieldValue, checkLengthAndRegex } from "../../utils/helper";
import { Dialog, DialogActions, DialogContent, HStack, ResponsiveBox, Stack } from "../primitives";


const RecallPendingApprovalsDetails = (props) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const {
        location: { state },
    } = props;

    const teReducer = useSelector( (state) => state.transactionEnquiryReducer );
    const [data, setData] = useState( teReducer?.recallPendingApprovalsData?.data
        ? teReducer?.recallPendingApprovalsData?.data.find(({ id }) => id === state.id)
        : {} );

    const user = useSelector((state) => state.loginUserReducer.user);
    const [roleType] = useState(user.role.role_type);

    const isWPBillerRole = roleType === RoleType.BILLER_ADMIN.type || roleType === RoleType.BILLER_USER.type
    const isWPFIOrERPAdminRole = roleType === RoleType.WP_FI_ADMIN.type || roleType === RoleType.WP_FI_USER.type || roleType === RoleType.ERPADMIN.type || roleType === RoleType.BILLADMIN.type;

    const errorRef = useRef();
    const successRef = useRef();
    const printComponentRef = useRef();

    const [isOpenDialog, setIsOpenDialog] = useState(false);
    const [recallAction, setRecallAction] = useState(null);
    const [errorMessage, setErrorMessage] = useState(null);
    const [showSuccessMessage, setShowSuccessMessage] = useState(false);
    const [loading, setLoading] = useState(null);
    const [rejectReason, setRejectReason] = useState("");
    const [rejectReasonError, setRejectReasonError] = useState("");
    const [isModified, setIsModified] = useState(false);

    useEffect(() => {
        if (showSuccessMessage && successRef.current)
            successRef.current.focus();
    }, [showSuccessMessage]);

    useEffect(() => {
        if (errorRef.current)
            errorRef.current.focus();
    }, [errorMessage?.length]);

    useEffect(() => {
        dispatch({ type: "UPDATE_TRANSACTION_FOR_PENDING_APPROVAL_SUCCESS", data: null })

        return onExit;  // clean up the ref on exit
    }, []);

    const onExit = () => {
        errorRef.current = null;
        successRef.current = null;
        printComponentRef.current = null;
    }

    useEffect(() => {
        if (loading === false) {
            if (teReducer.pendingApprovalError) {
                setErrorMessage(teReducer.pendingApprovalError);
            } else {
                let updatedRRP = teReducer.pendingApprovalData?.data;
                setShowSuccessMessage(true);
                setIsModified(true);

                const { recall_rejected_by_biller_reason, transaction_status, updatedDate, updatedBy } = updatedRRP;
                setData( { ...data, recall_rejected_by_biller_reason, transaction_status, updatedDate, updatedBy} );
            }
        }
    }, [loading]);

    const processPendingApproval = async () => {
        if (recallAction === "R" && !validateRejectReason())
            return;

        setIsOpenDialog(false);
        setLoading(true);
        if (showSuccessMessage)
            setShowSuccessMessage(false);

        let request = {
            action: recallAction,
            recall_reject_id: data.recall_reject_id,
            fi_number: data.fi_number
        };
        if (recallAction === "C") {
            request.correlation_id = data.correlation_id;
            request.payment_date = data.payment_date;
        } else if (recallAction === "R") {
            request.recall_reject_reason = rejectReason;
        }
        else if (recallAction === "RA") { //recall again
            request.correlation_id = data.correlation_id;
        }

        dispatch(
            actions.transactionEnquiryActions.processPendingApproval(
                {  ...request },
                { setLoading, t }
            )
        );
    };

    const validateRejectReason = () => {
        let errorMsg = checkFieldValue(rejectReason, "erps.rejectpaymentmerchants.page.label.reject.reason", true, (v) => Regex.regularText.test(v));
        setRejectReasonError(errorMsg);
        return "" === errorMsg;
    }

    const confirmCancelTransaction = () =>  {
        setRecallAction("C");
        setIsOpenDialog(true);
    };

    const confirmRejectRecall = () =>  {
        setRecallAction("R");
        setIsOpenDialog(true);
    };

    const confirmApproveRecall = () =>  {
        setRecallAction("A");
        setIsOpenDialog(true);
    };

    const confirmRecallAgain = () =>  {
        if (data?.has_recall_period_expired) {
            setErrorMessage(t("erps.recallpendingapprovals.recall.again.notAllowed"));
            return;
        }
        setRecallAction("RA");
        setIsOpenDialog(true);
    };

    const handleRejectReasonChange = (e) => {
        const value = e.target.value;
        let errMsg = checkLengthAndRegex(value, "erps.rejectpaymentmerchants.page.label.reject.reason", 80, Regex.regularText);
        // one invalid character will show in the field, but further input isn't allowed until the invalid character is removed
        setRejectReasonError(errMsg);
        if ("" === errMsg || Regex.regularText.test(rejectReason))  // if either current value is valid or previous value is valid
            setRejectReason(value);
    };

    const handlePrint = useReactToPrint({
        content: () => printComponentRef.current
    });

    const getSuccessMessageText = () => {
        if (recallAction === "C")
            return t("erps.recallpendingapprovals.recall.cancelled");
        if (recallAction === "R")
            return t("erps.recallpendingapprovals.recall.rejected");
        if (recallAction === "A")
            return t("erps.recallpendingapprovals.recall.approved");
        if (recallAction === "RA")
            return t("erps.recallpendingapprovals.recall.again");
        return "Unrecognized recallAction: " + recallAction;
    }

    const getConfirmationMessageText = () => {
        if (recallAction === "C")
            return t("erps.recallpendingapprovals.confirm.cancel.recall");
        if (recallAction === "R")
            return t("erps.recallpendingapprovals.confirm.reject.recall");
        if (recallAction === "A")
            return t("erps.recallpendingapprovals.confirm.approve.recall");
        if (recallAction === "RA")
            return t("erps.recallpaymentcustomers.confirm.recall.transaction");
        return "Unrecognized recallAction: " + recallAction;
    }

    const renderButtons = () => {
        if (isModified ||  ["A","C"].includes(data?.transaction_status) ||
            (["R", "E"].includes(data?.transaction_status) && isWPBillerRole)) {
            return <ERPSBaseButton                  // PRINT
                        variant="primary"
                        className="button_with_margin48"
                        label={t("erps.transactionEnquiry.page.transactionDetails.button.print")}
                        onClick={handlePrint}
                        loading={loading}
            />;
        } else if (isWPBillerRole) {
            return (
                <HStack spacing={2} ml={3}>
                    <ERPSBaseButton                 // REJECT (for Billers)
                        variant="primary"
                        className="button_with_margin48"
                        label={t("erps.common.button.reject")}
                        onClick={confirmRejectRecall}
                        loading={loading}
                    />
                    <ERPSBaseButton                 // APPROVE (for Billers)
                        variant="primary"
                        className="button_with_margin48"
                        label={t("erps.common.button.approve")}
                        onClick={confirmApproveRecall}
                        loading={loading}
                    />
                </HStack>
            );
        } else if (isWPFIOrERPAdminRole) {
            if (["R", "E"].includes(data?.transaction_status))
                return <ERPSBaseButton                  // RECALL AGAIN (for FI Users)
                    variant="primary"
                    className="button_with_margin48"
                    label={t("erps.recallpendingapprovals.label.recall.again")}
                    onClick={confirmRecallAgain}
                    loading={loading}
                />;
            else return <ERPSBaseButton                  // CANCEL (for FI Users)
                variant="primary"
                className="button_with_margin48"
                label={t("erps.recallpendingapprovals.label.cancel.recall.request")}
                onClick={confirmCancelTransaction}
                loading={loading}
            />;
        }
    };

    const detailArguments = {data: data, recallReason: data.recall_reject_reason, recalledBy: data.recalled_rejected_by};
    if (data?.transaction_status === "R")  // Recall request was rejected by a biller
        detailArguments.recallRejectedByBillerReason = data.recall_rejected_by_biller_reason;

    return (
        <div role="main">
            {showSuccessMessage && (
                <ConfirmationMessage
                    message={getSuccessMessageText()}
                    setShowMessage={setShowSuccessMessage}
                    inputRef={successRef}
                />
            )}
            <ErrorMessage 
              error={errorMessage}
              errorRef={errorRef}
              mt={2}
            />
            <ResponsiveBox sx={{ ml: 3 }}>
                <Breadcrumb
                    {...props}
                    fromDetailPage={true}
                />
            </ResponsiveBox>

            <ERPSTransactionDetails {...detailArguments} ref={printComponentRef}/>

            <HStack spacing={2} ml={3}>
                <ERPSBaseButton
                    variant="secondary"
                    className="button_with_margin48"
                    label={t("erps.newuser.button.back", "Back")}
                    onClick={() =>
                        props.history.push("/recall-pending-approvals/", {
                            fromDetailPage: true, isModified: isModified
                        })
                    }
                />
                {renderButtons()}
            </HStack>

            <Dialog
                open={isOpenDialog}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                fullWidth={true}
            >
                <DialogHeader
                    id="alert-dialog-title"
                    onClose={() => setIsOpenDialog(false)}
                    className={"box-dialog-header"}
                >
                    {t("erps.common.label.confirmation")}
                </DialogHeader>
                <DialogContent
                    id="alert-dialog-description"
                    className={"dialog-content-size"}
                >
                    {getConfirmationMessageText()}
                    {recallAction === "R" ? 
                        <ResponsiveBox sx={{mt: -2}}> 
                            <ERPSBaseTextField
                                id="rejectReason"
                                type="text"
                                label={t("erps.rejectpaymentmerchants.page.label.reject.reason")}
                                className="transactionEnquiry__textField__width"
                                size="small"
                                onChange={handleRejectReasonChange}
                                value={rejectReason}
                                required="true"
                                inputProps={{ maxLength: 80 }}
                                error={!!rejectReasonError}
                                helperText={rejectReasonError}
                            />
                        </ResponsiveBox>
                     : null}
                </DialogContent>
                <DialogActions className={"box-dialog-buttons"}>
                    <ERPSBaseButton
                        variant="secondary"
                        className="button_with_zeromargin"
                        onClick={() => setIsOpenDialog(false)}
                        label={t("erps.common.button.cancel")}
                    />
                    <ERPSBaseButton
                        variant="primary"
                        className="button_with_zeromargin"
                        onClick={processPendingApproval}
                        label={t("erps.welcome.page.button.Ok")}
                    />
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default withSidebar(RecallPendingApprovalsDetails);
